import { FiRewind } from 'react-icons/fi';

export const thirdConditionalData = {
  courseId: 'third-conditional',
  courseType: 'grammar',
  title: 'Third Conditional',
  icon: FiRewind,
  overview: {
    impact: 'Discuss unreal past situations and regrets',
    focus: 'If + past perfect, would have + past participle',
    uses: 'Speaking Part 3, Writing Task 2',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Structure',
      content: `<p><strong>If + past perfect, would have + past participle</strong></p><p>Used for situations that did NOT happen in the past.</p>`,
      examples: [
        {
          label: 'Regret',
          text: 'If I had studied harder, I would have passed the exam.'
        },
        {
          label: 'Imaginary past',
          text: 'If she had taken the earlier train, she wouldn\'t have been late.'
        }
      ]
    },
    {
      title: 'Contractions',
      content: `<p>In speech, these are heavily contracted:</p>`,
      points: [
        '\'If I\'d known, I\'d have helped.\' (If I had known, I would have helped.)',
        '\'She wouldn\'t\'ve come if she\'d known.\' (would not have)',
        'The contracted forms are natural in Speaking'
      ]
    }
  ],
  exercises: [
    {
      title: 'Complete the Third Conditional',
      type: 'select',
      instructions: 'Choose the correct form.',
      questions: [
        {
          text: 'If I ___ (know), I would have told you.',
          options: [
            'had known',
            'knew',
            'have known'
          ],
          answer: 'had known',
          explanation: 'Third conditional if-clause uses past perfect.'
        },
        {
          text: 'She ___ (not miss) the flight if she had left earlier.',
          options: [
            'wouldn\'t have missed',
            'didn\'t miss',
            'won\'t miss'
          ],
          answer: 'wouldn\'t have missed',
          explanation: 'Main clause: would(n\'t) have + past participle.'
        }
      ]
    },
    {
      title: 'Complete Third Conditionals',
      type: 'fill-in',
      instructions: 'Fill in the blank to complete each third conditional sentence.',
      questions: [
        {
          text: 'If I had studied harder, I would have ___ the exam.',
          answer: 'passed',
          explanation: 'Would have + past participle: \'passed\' completes the third conditional result clause.'
        },
        {
          text: 'If they had ___ earlier, they wouldn\'t have missed the train.',
          answer: 'left',
          explanation: 'The if-clause needs a past participle after \'had\': \'left\'.'
        },
        {
          text: 'She would have ___ the job if she had prepared for the interview.',
          answer: 'got',
          explanation: 'Would have + past participle: \'got\' (or \'gotten\') completes the result clause.'
        },
        {
          text: 'If we hadn\'t taken a taxi, we would have ___ late.',
          answer: 'been',
          explanation: 'Would have + past participle: \'been\' completes \'would have been late\'.'
        }
      ]
    },
    {
      title: 'Choose the Correct Form',
      type: 'select',
      instructions: 'Select the grammatically correct third conditional sentence.',
      questions: [
        {
          text: 'Which sentence is correct?',
          options: [
            'If I had known, I would have helped.',
            'If I would have known, I had helped.',
            'If I knew, I would have helped.'
          ],
          answer: 'If I had known, I would have helped.',
          explanation: 'Third conditional: if + past perfect (had known), would have + past participle (helped).'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'If she had saved money, she could have bought a car.',
            'If she saved money, she could have bought a car.',
            'If she had saved money, she can buy a car.'
          ],
          answer: 'If she had saved money, she could have bought a car.',
          explanation: '\'Could have + past participle\' is also valid in third conditional result clauses.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'He wouldn\'t have been injured if he had worn a seatbelt.',
            'He wouldn\'t have been injured if he wore a seatbelt.',
            'He won\'t have been injured if he had worn a seatbelt.'
          ],
          answer: 'He wouldn\'t have been injured if he had worn a seatbelt.',
          explanation: 'Both clauses must follow the third conditional pattern: would(n\'t) have + PP / had + PP.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'If they had listened to the forecast, they might have brought umbrellas.',
            'If they listened to the forecast, they might have brought umbrellas.',
            'If they had listened to the forecast, they might bring umbrellas.'
          ],
          answer: 'If they had listened to the forecast, they might have brought umbrellas.',
          explanation: '\'Might have + past participle\' expresses uncertain past outcomes in the third conditional.'
        }
      ]
    },
    {
      title: 'Write Third Conditionals',
      type: 'writing',
      instructions: 'Write a full third conditional sentence about each situation. Use the structure: If + past perfect, would/could/might have + past participle.',
      questions: [
        {
          text: 'You didn\'t wake up on time and missed your flight. Write a sentence expressing regret.',
          sampleAnswer: 'If I had woken up on time, I wouldn\'t have missed my flight.',
          explanation: 'Express regret about the past: the if-clause states the unreal condition, and the result clause shows what would have been different.'
        },
        {
          text: 'Your friend didn\'t apply for the scholarship. She didn\'t get funding. Write a third conditional.',
          sampleAnswer: 'If she had applied for the scholarship, she might have got the funding.',
          explanation: 'Use \'might have\' when the result is uncertain. The if-clause uses past perfect (had applied).'
        },
        {
          text: 'You forgot to charge your phone. It died during an important call. Write about what could have been different.',
          sampleAnswer: 'If I had charged my phone, it wouldn\'t have died during the important call.',
          explanation: 'The third conditional links an unreal past condition to its imaginary result using had + PP and would(n\'t) have + PP.'
        }
      ]
    }
  ],
  tips: [
    'If-clause: past perfect (had + past participle)',
    'Main clause: would have + past participle',
    'Expresses regret or imaginary past',
    'Never use \'would have\' in the if-clause'
  ],
  dailyPractice: [
    'Practice third conditional concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Third Conditional: If + past perfect, would have + past participle. For unreal/imaginary past situations and regrets.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...thirdConditionalData,
      title: thirdConditionalData.title + ' (Foundations)',
      overview: { ...thirdConditionalData.overview, duration: '15-20 minutes' },
      sections: thirdConditionalData.sections.slice(0, Math.max(2, Math.ceil(thirdConditionalData.sections.length / 2))),
      exercises: thirdConditionalData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...thirdConditionalData,
      title: thirdConditionalData.title + ' (Advanced)',
      overview: { ...thirdConditionalData.overview, duration: '30-35 minutes' },
      sections: thirdConditionalData.sections,
      exercises: thirdConditionalData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(thirdConditionalData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return thirdConditionalData;
}
